import React, { useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Formik, Form } from 'formik';
import { Stack, Typography } from '@mui/material';
import Button from '~/components/common/Button';
import { Field } from '~/components/common/Formik';
import { fromQueryString } from '~/utils/queryString';
import themes, { styled } from '~/themes';
import { debounce, isEqual, orderBy } from 'lodash';
import { Box } from '@mui/system';
import { useGetTeamsQuery } from '~/services/api/shared';
import {
  CustomerSupportOptions,
  StatusOptions,
  ShiftScheduleOptions,
} from '../constants';

export const StyledFilterStack = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  rowGap: theme.spacing(2),
  columnGap: theme.spacing(0.8),
  [themes.breakpoints.down('xl')]: {
    rowGap: theme.spacing(1),
  },
}));

interface FilterFormProps {
  onSubmit?: (values: any) => void;
  onClear?: () => void;
}

const defaultValues = {
  search: '',
  driver_status: '',
  team_id: '',
  customer_support: '',
  shift_schedule: '',
};

export const StyledForm = styled(Form)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    width: '100%',
  },
}));

export const AddressFilterForm: React.FC<FilterFormProps> = ({
  onSubmit,
  onClear,
}) => {
  const formRef = useRef<any>(null);
  const location = useLocation();

  const { search, driver_status, team_id, customer_support, shift_schedule } =
    fromQueryString(location.search, { arrayFormat: 'none' });

  const { data: listTeam } = useGetTeamsQuery({
    enabled: true,
    params: {
      search: '',
    },
  });

  const teamOptions = useMemo(() => {
    const mapped =
      listTeam?.map((item) => ({
        label: item.name,
        value: item.id,
      })) || [];

    return orderBy(mapped, [(o) => o.label.toLowerCase()], ['asc']);
  }, [listTeam]);

  const initialValues = {
    search: search || '',
    driver_status: driver_status || '',
    team_id: team_id || '',
    customer_support: customer_support || '',
    shift_schedule: shift_schedule || '',
  };

  const handleSearch = useCallback(
    debounce(() => {
      formRef.current.handleSubmit();
    }, 600),
    [],
  );

  const renderStatus = useCallback(
    ({ setFieldValue }) => (
      <Field.Select
        key='driver_status'
        placeholder='Status'
        name='driver_status'
        options={StatusOptions}
        sxPlaceholder={{
          color: `${themes.color.grayPlaceHolder} !important`,
          textOverflow: 'ellipsis',
          overflow: 'hidden',
        }}
        onChange={() => {
          formRef.current.handleSubmit();
        }}
        onClear={() => {
          setFieldValue('driver_status', '');
          formRef.current.handleSubmit();
        }}
      />
    ),
    [StatusOptions],
  );

  const renderTeam = useCallback(
    ({ setFieldValue }) => (
      <Field.Select
        key='team_id'
        placeholder='Team'
        name='team_id'
        options={teamOptions}
        sortOption
        sxPlaceholder={{
          color: `${themes.color.grayPlaceHolder} !important`,
          textOverflow: 'ellipsis',
          overflow: 'hidden',
        }}
        onChange={() => {
          formRef.current.handleSubmit();
        }}
        onClear={() => {
          setFieldValue('team_id', '');
          formRef.current.handleSubmit();
        }}
      />
    ),
    [teamOptions],
  );

  const renderShiftSchedule = useCallback(
    ({ setFieldValue }) => (
      <Field.Select
        key='shift_schedule'
        placeholder='Shift schedule'
        name='shift_schedule'
        options={ShiftScheduleOptions}
        sxPlaceholder={{
          color: `${themes.color.grayPlaceHolder} !important`,
          textOverflow: 'ellipsis',
          overflow: 'hidden',
        }}
        onChange={() => {
          formRef.current.handleSubmit();
        }}
        onClear={() => {
          setFieldValue('shift_schedule', '');
          formRef.current.handleSubmit();
        }}
      />
    ),
    [ShiftScheduleOptions],
  );

  const renderCustomerSupport = useCallback(
    ({ setFieldValue }) => (
      <Field.Select
        key='customer_support'
        placeholder='Customer support option'
        name='customer_support'
        options={CustomerSupportOptions}
        sxPlaceholder={{
          color: `${themes.color.grayPlaceHolder} !important`,
          textOverflow: 'ellipsis',
          overflow: 'hidden',
        }}
        onChange={() => {
          formRef.current.handleSubmit();
        }}
        onClear={() => {
          setFieldValue('customer_support', '');
          formRef.current.handleSubmit();
        }}
      />
    ),
    [CustomerSupportOptions],
  );

  const renderForm = ({ values, setFieldValue, resetForm }) => (
    <StyledFilterStack>
      <Stack
        sx={{
          width: 300,
          [themes.breakpoints.down('md')]: {
            width: 'unset',
            flex: 1,
          },
        }}
      >
        <Field.SearchInput
          name='search'
          placeholder='Search'
          onClear={() => {
            setFieldValue('search', '');
            formRef.current.handleSubmit();
          }}
          onChange={(e) => {
            setFieldValue('search', e.target.value);
            handleSearch();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              formRef.current.handleSubmit();
            }
          }}
        />
      </Stack>
      <Stack
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          rowGap: 2,
          columnGap: 0.8,
          [themes.breakpoints.down('md')]: {
            rowGap: themes.spacing(1),
            flexWrap: 'wrap',
          },
        }}
      >
        <Box
          sx={{
            width: 160,
            [themes.breakpoints.down('md')]: {
              width: 'calc(50% - 4px)',
            },
          }}
        >
          {renderStatus({ setFieldValue })}
        </Box>
        <Box
          sx={{
            width: 160,
            [themes.breakpoints.down('md')]: {
              width: 'calc(50% - 4px)',
            },
          }}
        >
          {renderTeam({ setFieldValue })}
        </Box>
        <Box
          sx={{
            width: 160,
            [themes.breakpoints.down('md')]: {
              width: 'calc(50% - 4px)',
            },
          }}
        >
          {renderCustomerSupport({ setFieldValue })}
        </Box>
        <Box
          sx={{
            width: 160,
            [themes.breakpoints.down('md')]: {
              width: 'calc(50% - 4px)',
            },
          }}
        >
          {renderShiftSchedule({ setFieldValue })}
        </Box>
        {!isEqual(values, defaultValues) && (
          <Button
            buttonType='text'
            onClick={() => {
              resetForm();
              onClear();
            }}
            sx={{
              '&.MuiButton-text': {
                minWidth: 'unset !important',
              },
            }}
          >
            <Typography
              sx={{
                color: themes.color.violet500,
                fontWeight: 500,
              }}
            >
              Clear
            </Typography>
          </Button>
        )}
      </Stack>
    </StyledFilterStack>
  );

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      enableReinitialize
      innerRef={formRef}
    >
      {({ values, setFieldValue, resetForm }) =>
        renderForm({ values, setFieldValue, resetForm })
      }
    </Formik>
  );
};
